import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { SupportPolicy } from '@provision/entity-types';
import { ServiceLevel } from '@provision/service-level-utils';
import {
  EscalationRule,
  parseEscalationRules,
  SERVICE_LEVEL_PRIORITY_ORDER,
} from '@provision/sidekick-utils';
import dayjs, { Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { isNullish } from 'remeda';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { useUserGroupOptions } from 'src/graphql';
import { SignalUrgencyLevel } from 'src/graphql/entities/signal-business-hours/signal-business-hours.types';
import { deserializeSupportPolicyConditions } from 'src/graphql/entities/support-policy/support-policy.conditions';
import {
  NormalizedSupportPolicy,
  normalizeSupportPolicyScopeFields,
} from 'src/graphql/entities/support-policy/support-policy.utils';
import { getDefaultEscalationTitle } from './escalation-defaults';
import { EscalationRuleModal } from './escalation-rule-modal';
import {
  ESCALATION_URGENCY_DISPLAY_LABELS,
  formatEnumLabel,
  formatServiceLevelLabel,
  serializeEscalationRules,
} from './escalation-utils';
import { POLICY_CATEGORY_ESCALATION } from './policy-constants';
import { SupportPolicyFormValues } from './policy-form';
import { RuleMatchPreview } from './rules-match-preview';
import { useEscalationEvaluation } from './use-escalation-evaluation';

interface EscalationPolicyFormProps {
  policy?: Partial<SupportPolicy> | Partial<NormalizedSupportPolicy>;
  onSave?: (values: SupportPolicyFormValues) => void;
  formId?: string;
}

export const EscalationPolicyForm = ({
  policy,
  onSave,
  formId,
}: EscalationPolicyFormProps) => {
  const partnerId = (policy as Partial<NormalizedSupportPolicy>)?.partnerId;
  const evalCtx = useEscalationEvaluation(partnerId);
  const enumLabelFormatter = formatEnumLabel as (value: string) => string;
  const { userGroupOptions } = useUserGroupOptions();
  const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
  const [editingRuleIndex, setEditingRuleIndex] = useState<number | null>(null);
  const [previewServiceLevel, setPreviewServiceLevel] = useState<string>(
    SERVICE_LEVEL_PRIORITY_ORDER.at(-1) ?? ServiceLevel.Signature,
  );
  const [previewTime, setPreviewTime] = useState<Dayjs>(dayjs());

  const defaultRules = useMemo(() => parseEscalationRules(policy?.escalationRules), [policy]);

  const scope = useMemo(() => normalizeSupportPolicyScopeFields(policy), [policy]);

  const initialValidUntil = policy?.validUntil
    ? dayjs(policy.validUntil as string | Date)
    : null;

  const { control, handleSubmit, setValue } = useForm<SupportPolicyFormValues>({
    defaultValues: {
      title: policy?.title ?? '',
      description: policy?.description ?? '',
      type: policy?.type ?? 'requirement',
      category: POLICY_CATEGORY_ESCALATION,
      pinnedAt: Boolean(policy?.pinnedAt),
      validUntil: initialValidUntil,
      userGroups: policy?.userGroups?.map((group) => group.id) ?? [],
      addressIds: scope.addressIds,
      clientIds: scope.clientIds,
      clientToAddressIds: scope.clientToAddressIds,
      enableConditions: Boolean(policy?.conditions),
      conditions: deserializeSupportPolicyConditions(policy?.conditions),
      urgencyLevel: policy?.urgencyLevel ?? SignalUrgencyLevel.Normal,
      escalationRules: serializeEscalationRules(defaultRules),
    },
  });

  const rawEscalationRules = useWatch({ control, name: 'escalationRules' });
  const urgencyLevel = useWatch({ control, name: 'urgencyLevel' });

  const escalationRules = useMemo(
    () => parseEscalationRules(rawEscalationRules),
    [rawEscalationRules],
  );

  const urgencyLabel =
    ESCALATION_URGENCY_DISPLAY_LABELS[urgencyLevel ?? ''] ?? urgencyLevel ?? 'Escalation';

  const handleRuleSave = (rule: EscalationRule) => {
    const nextRules = [...escalationRules];
    if (isNullish(editingRuleIndex)) {
      nextRules.push(rule);
    } else {
      nextRules[editingRuleIndex] = rule;
    }
    setValue('escalationRules', serializeEscalationRules(nextRules), { shouldDirty: true });
    setEditingRuleIndex(null);
    setIsRuleModalOpen(false);
  };

  const handleFormSubmit = (values: SupportPolicyFormValues) => {
    const rules = parseEscalationRules(values.escalationRules);
    const autoTitle = getDefaultEscalationTitle(
      values.urgencyLevel ?? SignalUrgencyLevel.Normal,
    );
    const autoDescription = generateDescriptionFromRules(rules);

    onSave?.({
      ...values,
      title: autoTitle,
      description: autoDescription,
    });
  };

  const currentRule = isNullish(editingRuleIndex) ? null : escalationRules[editingRuleIndex];

  return (
    <>
      <Box component="form" id={formId} onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle1" fontWeight={600}>
            {urgencyLabel} Policy
          </Typography>

          <TextField
            size="small"
            label="Urgency level"
            value={urgencyLabel}
            disabled
            slotProps={{ input: { readOnly: true } }}
          />

          <Controller
            name="userGroups"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                SelectProps={{ multiple: true }}
                size="small"
                label="Available To"
                value={field.value ?? []}
              >
                {userGroupOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1.5,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle2">Response Rules</Typography>
                <Typography variant="caption" color="text.secondary">
                  Rules are evaluated top-to-bottom. First match wins.
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setEditingRuleIndex(null);
                  setIsRuleModalOpen(true);
                }}
              >
                Add Rule
              </Button>
            </Stack>

            <Stack spacing={1} sx={{ mt: 1 }}>
              {escalationRules.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No rules configured. Click &quot;Add Rule&quot; to get started.
                </Typography>
              ) : null}

              {escalationRules.map((rule, index) => (
                <Box
                  key={rule.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      {rule.name ? (
                        <Typography variant="body2" fontWeight={500} sx={{ mb: 0.25 }}>
                          {rule.name}
                        </Typography>
                      ) : null}
                      <Typography
                        variant="body2"
                        color={rule.name ? 'text.secondary' : undefined}
                      >
                        If <strong>{formatServiceLevelLabel(rule.serviceLevels)}</strong>{' '}
                        escalates{' '}
                        <strong>{enumLabelFormatter(String(rule.timeCondition.type))}</strong>,
                        then <strong>{getRuleResponseLabel(rule)}</strong>
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                      <IconButton
                        size="small"
                        disabled={index === 0}
                        onClick={() =>
                          setValue(
                            'escalationRules',
                            serializeEscalationRules(
                              moveRule(escalationRules, index, index - 1),
                            ),
                          )
                        }
                      >
                        <DynamicIcon name="keyboard_arrow_up" />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={index === escalationRules.length - 1}
                        onClick={() =>
                          setValue(
                            'escalationRules',
                            serializeEscalationRules(
                              moveRule(escalationRules, index, index + 1),
                            ),
                          )
                        }
                      >
                        <DynamicIcon name="keyboard_arrow_down" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingRuleIndex(index);
                          setIsRuleModalOpen(true);
                        }}
                      >
                        <DynamicIcon name="edit" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() =>
                          setValue(
                            'escalationRules',
                            serializeEscalationRules(
                              escalationRules.filter((_, idx) => idx !== index),
                            ),
                          )
                        }
                      >
                        <DynamicIcon name="delete" />
                      </IconButton>
                    </Stack>
                  </Stack>

                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Chip size="small" label={`Order ${index + 1}`} />
                    <Chip
                      size="small"
                      label={enumLabelFormatter(String(rule.response.type))}
                      variant="outlined"
                    />
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>

          <RuleMatchPreview
            rules={escalationRules}
            serviceLevel={previewServiceLevel}
            onServiceLevelChange={setPreviewServiceLevel}
            currentTime={previewTime}
            onCurrentTimeChange={setPreviewTime}
            timezone={evalCtx.timezone}
            businessHours={evalCtx.businessHours}
            escalationCutoffHours={evalCtx.escalationCutoffHours}
            holidays={evalCtx.holidays}
          />
        </Stack>
      </Box>

      <EscalationRuleModal
        key={editingRuleIndex ?? 'new'}
        open={isRuleModalOpen}
        initialRule={currentRule}
        order={editingRuleIndex ?? escalationRules.length}
        urgencyLevel={urgencyLevel ?? SignalUrgencyLevel.Normal}
        onClose={() => {
          setEditingRuleIndex(null);
          setIsRuleModalOpen(false);
        }}
        onSave={handleRuleSave}
      />
    </>
  );
};

function moveRule(rules: EscalationRule[], from: number, to: number): EscalationRule[] {
  const next = [...rules];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next.map((rule, index) => ({ ...rule, order: index }));
}

function getRuleResponseLabel(rule: EscalationRule): string {
  switch (rule.response.type) {
    case 'fixed_duration':
      return `within ${rule.response.durationMinutes ?? 0} minutes`;
    case 'by_end_of_day':
      return 'by end of day';
    case 'by_fixed_time':
      return `by ${rule.response.time ?? '0:00'}`;
    case 'by_next_business_day':
      return `by ${rule.response.time ?? '0:00'} next business day`;
    case 'by_tomorrow':
      return `by ${rule.response.time ?? '0:00'} tomorrow`;
    case 'best_effort':
      return 'best effort';
    default:
      return 'best effort';
  }
}

function generateDescriptionFromRules(rules: EscalationRule[]): string {
  if (rules.length === 0) return '';

  const bullets = rules.map((rule) => {
    const serviceLevelText = formatServiceLevelLabel(rule.serviceLevels);
    const timeText = formatEnumLabel(rule.timeCondition.type);
    const responseText = getRuleResponseLabel(rule);
    return `<li>${serviceLevelText} (${timeText}): ${responseText}</li>`;
  });

  return `<ul>${bullets.join('')}</ul>`;
}
